/**
 * Domain enumerations. Names are kept identical to the Prisma enums so values
 * are interchangeable between the database layer, the API and the web client.
 */

export const Role = {
  PLAYER: 'PLAYER',
  ADMINISTRATOR: 'ADMINISTRATOR',
  SUPER_ADMINISTRATOR: 'SUPER_ADMINISTRATOR',
} as const;
export type Role = (typeof Role)[keyof typeof Role];

/** TZ §4 */
export const UserStatus = {
  INVITED: 'INVITED',
  ACTIVE: 'ACTIVE',
  BLOCKED: 'BLOCKED',
  INACTIVE: 'INACTIVE',
} as const;
export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];

export const InvitationStatus = {
  PENDING: 'PENDING',
  ACCEPTED: 'ACCEPTED',
  REVOKED: 'REVOKED',
  EXPIRED: 'EXPIRED',
} as const;
export type InvitationStatus = (typeof InvitationStatus)[keyof typeof InvitationStatus];

/** TZ §33 */
export const RoundStatus = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
} as const;
export type RoundStatus = (typeof RoundStatus)[keyof typeof RoundStatus];

/** TZ §32 */
export const MatchStatus = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
  RESULT_ENTERED: 'RESULT_ENTERED',
  CALCULATED: 'CALCULATED',
  CANCELLED: 'CANCELLED',
} as const;
export type MatchStatus = (typeof MatchStatus)[keyof typeof MatchStatus];

/** Statuses a match can never leave (TZ §22). */
export const TERMINAL_MATCH_STATUSES: readonly MatchStatus[] = [
  MatchStatus.CALCULATED,
  MatchStatus.CANCELLED,
];

/** TZ §15 */
export const MatchOutcome = {
  HOME_WIN: 'HOME_WIN',
  DRAW: 'DRAW',
  AWAY_WIN: 'AWAY_WIN',
} as const;
export type MatchOutcome = (typeof MatchOutcome)[keyof typeof MatchOutcome];

/** TZ §18 */
export const LedgerTransactionType = {
  ADMIN_DEPOSIT: 'ADMIN_DEPOSIT',
  ADMIN_WITHDRAWAL: 'ADMIN_WITHDRAWAL',
  BET_ENTRY: 'BET_ENTRY',
  ROUND_WIN: 'ROUND_WIN',
  REFUND: 'REFUND',
  ROUND_REFUND: 'ROUND_REFUND',
  REVERSAL: 'REVERSAL',
  MANUAL_ADJUSTMENT: 'MANUAL_ADJUSTMENT',
} as const;
export type LedgerTransactionType =
  (typeof LedgerTransactionType)[keyof typeof LedgerTransactionType];

/** TZ §19 */
export const PlatformLedgerTransactionType = {
  COMMISSION: 'COMMISSION',
  ROUNDING_REMAINDER: 'ROUNDING_REMAINDER',
  REVERSAL: 'REVERSAL',
  MANUAL_ADJUSTMENT: 'MANUAL_ADJUSTMENT',
} as const;
export type PlatformLedgerTransactionType =
  (typeof PlatformLedgerTransactionType)[keyof typeof PlatformLedgerTransactionType];

/** TZ §21, §27 — a calculation is never deleted, only superseded. */
export const CalculationStatus = {
  COMPLETED: 'COMPLETED',
  REVERSED: 'REVERSED',
} as const;
export type CalculationStatus = (typeof CalculationStatus)[keyof typeof CalculationStatus];

/** TZ §15 */
export const SettlementResultStatus = {
  WINNERS: 'WINNERS',
  NO_WINNER: 'NO_WINNER',
  /** Единственный участник — полный возврат без комиссии. */
  SINGLE_PARTICIPANT: 'SINGLE_PARTICIPANT',
} as const;
export type SettlementResultStatus =
  (typeof SettlementResultStatus)[keyof typeof SettlementResultStatus];

/**
 * TZ §29 lists email as a channel, but this installation has no email at all:
 * identity is the phone number, so transactional messages go out over SMS.
 */
export const NotificationChannel = {
  SMS: 'SMS',
  TELEGRAM: 'TELEGRAM',
} as const;
export type NotificationChannel = (typeof NotificationChannel)[keyof typeof NotificationChannel];

/**
 * TZ §29.
 *
 * Invitation and password-reset messages are deliberately absent. Those carry a
 * one-time code, and the notification outbox persists its payload as JSON — so
 * routing them through it would store the plaintext code in the database and
 * undo the point of hashing it. They are sent directly instead; see
 * `SmsPurpose`.
 */
export const NotificationEvent = {
  PREDICTION_ACCEPTED: 'PREDICTION_ACCEPTED',
  PREDICTION_UPDATED: 'PREDICTION_UPDATED',
  ROUND_RESULT: 'ROUND_RESULT',
  MATCH_CANCELLED: 'MATCH_CANCELLED',
  REFUND: 'REFUND',
  BALANCE_DEPOSIT: 'BALANCE_DEPOSIT',
  BALANCE_WITHDRAWAL: 'BALANCE_WITHDRAWAL',
} as const;
export type NotificationEvent = (typeof NotificationEvent)[keyof typeof NotificationEvent];

/** Access messages, sent straight to the gateway and never stored as payload. */
export const SmsPurpose = {
  INVITATION: 'INVITATION',
  PASSWORD_RESET: 'PASSWORD_RESET',
} as const;
export type SmsPurpose = (typeof SmsPurpose)[keyof typeof SmsPurpose];

export const NotificationStatus = {
  PENDING: 'PENDING',
  SENT: 'SENT',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
} as const;
export type NotificationStatus = (typeof NotificationStatus)[keyof typeof NotificationStatus];

/** TZ §28 — the minimum set of auditable actions. */
export const AuditAction = {
  INVITATION_CREATED: 'INVITATION_CREATED',
  INVITATION_REISSUED: 'INVITATION_REISSUED',
  INVITATION_REVOKED: 'INVITATION_REVOKED',
  INVITED_PLAYER_DELETED: 'INVITED_PLAYER_DELETED',
  USER_REGISTERED: 'USER_REGISTERED',
  USER_BLOCKED: 'USER_BLOCKED',
  USER_UNBLOCKED: 'USER_UNBLOCKED',
  /** Historical: players used to be promoted; kept so old audit rows still read. */
  ADMINISTRATOR_GRANTED: 'ADMINISTRATOR_GRANTED',
  ADMINISTRATOR_REVOKED: 'ADMINISTRATOR_REVOKED',
  STAFF_CREATED: 'STAFF_CREATED',
  STAFF_UPDATED: 'STAFF_UPDATED',
  SETTINGS_UPDATED: 'SETTINGS_UPDATED',
  TEAM_CREATED: 'TEAM_CREATED',
  TEAM_UPDATED: 'TEAM_UPDATED',
  ROUND_CREATED: 'ROUND_CREATED',
  ROUND_UPDATED: 'ROUND_UPDATED',
  ROUND_FINALIZED: 'ROUND_FINALIZED',
  MATCH_CREATED: 'MATCH_CREATED',
  MATCH_UPDATED: 'MATCH_UPDATED',
  MATCH_CANCELLED: 'MATCH_CANCELLED',
  MATCH_RESULT_ENTERED: 'MATCH_RESULT_ENTERED',
  MATCH_RESULT_UPDATED: 'MATCH_RESULT_UPDATED',
  CALCULATION_STARTED: 'CALCULATION_STARTED',
  CALCULATION_COMPLETED: 'CALCULATION_COMPLETED',
  CALCULATION_REVERSED: 'CALCULATION_REVERSED',
  CALCULATION_RECALCULATED: 'CALCULATION_RECALCULATED',
  ADMIN_BALANCE_DEPOSIT: 'ADMIN_BALANCE_DEPOSIT',
  ADMIN_BALANCE_WITHDRAWAL: 'ADMIN_BALANCE_WITHDRAWAL',
} as const;
export type AuditAction = (typeof AuditAction)[keyof typeof AuditAction];

/** TZ §25 — per-player outcome inside a round summary. */
export const PlayerMatchOutcomeKind = {
  WIN: 'WIN',
  LOSS: 'LOSS',
  REFUND: 'REFUND',
  PENDING: 'PENDING',
} as const;
export type PlayerMatchOutcomeKind =
  (typeof PlayerMatchOutcomeKind)[keyof typeof PlayerMatchOutcomeKind];
